import { Box, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EventTimelineEntry from './event-timeline-entry/event-timeline-entry.component';
import { EventTimelineEntry as EventTimelineEntryType } from './event-timeline.types';

type EventTimelineProps = {
  title: string;
  entries: EventTimelineEntryType[];
  isLoading?: boolean;
  emptyMessage?: string;
  'data-testid'?: string;
};

const sortNewestFirst = (entries: EventTimelineEntryType[]) =>
  [...entries].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

const EventTimeline = ({
  title,
  entries,
  isLoading = false,
  emptyMessage,
  'data-testid': dataTestId = 'EventTimeline',
}: EventTimelineProps) => {
  const { t } = useTranslation();
  if (isLoading) return null;

  const sortedEntries = sortNewestFirst(entries);

  return (
    <Box data-testid={dataTestId}>
      <Divider />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={2}
        mb={2}
      >
        <Typography variant="h5">{title}</Typography>
        {sortedEntries.length > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`${dataTestId}.EventsCount`}
          >
            {t('EventTimeline.eventsCount', { count: sortedEntries.length })}
          </Typography>
        )}
      </Box>
      {sortedEntries.length === 0 && emptyMessage && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={`${dataTestId}.EmptyMessage`}
        >
          {emptyMessage}
        </Typography>
      )}
      {sortedEntries.map((entry, index) => (
        <EventTimelineEntry
          key={entry.id}
          entry={entry}
          isLast={index === sortedEntries.length - 1}
        />
      ))}
    </Box>
  );
};

export default EventTimeline;
